import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  FiHome as DashboardIcon,
  FiBook as ClassesIcon,
  FiFileText as SubmissionsIcon,
  FiHelpCircle as HelpIcon,
  FiSettings as SettingsIcon,
  FiUsers as UsersIcon,
  FiLogOut as LogoutIcon,
  FiList as QuestionsIcon,
  FiGlobe as SchoolsIcon,
  FiMoreVertical as MoreIcon,
  FiPlus as PlusIcon,
  FiUser as UserIcon,
  FiUpload as UploadIcon
} from 'react-icons/fi';
import { FaPoundSign as PaymentsIcon } from 'react-icons/fa';
import { DropdownMenu } from '@radix-ui/themes';

// Import logo and components
import snapgradeLogo from '../../img/snapgrade-logo.png';
import defaultSchoolLogo from '../../img/default-school.png';
import HelpPopup from '../ui/HelpPopup/HelpPopup';
import SettingsPopup from '../ui/SettingsPopup/SettingsPopup';

// Types
interface SidebarProps {
  userName?: string;
  role?: string;
}

interface User {
  name?: string;
  role?: string;
  school?: string | { name: string };
  permissions?: string[];
  schoolType?: string;
  plan?: string;
  schoolCode?: string;
}

interface NavigationItem {
  path: string;
  icon: React.ComponentType<any>;
  label: string;
  visible: boolean;
}

// Default school logo for fallback
const DEFAULT_SCHOOL_LOGO = defaultSchoolLogo;

// Map of school names to their logo URLs (empty object as default)
const schoolLogos: Record<string, string> = {};

// Role badge color mapping
const getRoleBadgeColor = (role?: string): string => {
  switch (role?.toLowerCase()) {
    case 'student':
      return '#4ade80';
    case 'teacher':
      return '#60a5fa';
    case 'admin':
      return '#f87171';
    case 'owner':
      return '#8b5cf6';
    default:
      return '#9ca3af';
  }
};

// Inline styles to override any CSS conflicts
const sidebarStyles = {
  desktop: {
    position: 'fixed' as const,
    left: 0,
    top: 0,
    bottom: 0,
    width: '256px',
    backgroundColor: '#f2d13e',
    borderRight: '1px solid #e5e7eb',
    display: 'flex',
    flexDirection: 'column' as const,
    zIndex: 1000,
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
  },
  mobile: {
    position: 'fixed' as const,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#f2d13e',
    height: '64px',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 -2px 4px rgba(0, 0, 0, 0.1)'
  },
  header: {
    padding: '24px',
    borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
  },
  logo: {
    width: '100%',
    maxWidth: '200px',
    height: 'auto',
    objectFit: 'contain' as const
  },
  schoolLogo: {
    width: '48px',
    height: '48px',
    objectFit: 'scale-down' as const,
    marginTop: '8px'
  },
  nav: {
    flex: 1,
    padding: '16px'
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 12px',
    borderRadius: '8px',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'background-color 0.2s',
    marginBottom: '4px',
    color: '#374151'
  },
  navItemActive: {
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    color: '#000000'
  },
  navItemHover: {
    backgroundColor: 'rgba(0, 0, 0, 0.05)'
  },
  supportSection: {
    marginTop: '32px'
  },
  supportTitle: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#6b7280',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    padding: '0 12px',
    marginBottom: '12px'
  },
  userProfile: {
    padding: '16px',
    borderTop: '1px solid rgba(0, 0, 0, 0.1)'
  },
  avatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: '#d1d5db',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151'
  },
  roleBadge: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    marginRight: '8px'
  }
};

const Sidebar: React.FC<SidebarProps> = ({ userName, role: propRole }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showHelpPopup, setShowHelpPopup] = useState<boolean>(false);
  const [showSettingsPopup, setShowSettingsPopup] = useState<boolean>(false);
  const [isMobileView, setIsMobileView] = useState<boolean>(window.innerWidth <= 768);
  
  // Handle user data from props or localStorage
  const userDataString = localStorage.getItem('user');
  const user: User | null = userDataString ? JSON.parse(userDataString) : null;
  
  // Use prop role if provided, otherwise get from user data or localStorage
  const userRole = propRole || user?.role?.toLowerCase() || '';
  const displayName = userName || user?.name || '';
  
  // Check if user has payment permissions
  const hasPaymentPermissions = user?.permissions?.includes('view_payments') || 
                               user?.permissions?.includes('manage_payments');
  
  // Check if this is a Personal Learning Plan or Classroom Success Plan teacher
  const isSubscriptionPlanTeacher = userRole === 'teacher' && 
    (user?.schoolType === 'personal' || user?.schoolType === 'classroom' || 
     user?.plan === 'personal' || user?.plan === 'classroom');
  
  // Check if this is a Classroom Admin
  const isClassroomAdmin = userRole === 'admin' && 
    (user?.schoolType === 'classroom' || user?.plan === 'classroom');
  
  // Show payments if user has explicit permissions or matches expected role/school type
  // Explicitly exclude students from seeing payments
  const showPayments = userRole !== 'student' && (hasPaymentPermissions || isSubscriptionPlanTeacher || isClassroomAdmin);
  
  // Add resize listener to detect mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userName');
    localStorage.removeItem('role');
    navigate('/login');
  };

  const isActive = (path: string): boolean => {
    return location.pathname === path || 
           (path !== getDashboardPath() && location.pathname.startsWith(path));
  };

  const getSubmissionsPath = (): string => {
    switch (userRole) {
      case 'teacher':
        return '/submissions';
      case 'student':
        return '/my-submissions';
      case 'admin':
        return '/all-submissions';
      default:
        return '/submissions';
    }
  };

  const getClassesPath = (): string => {
    switch (userRole) {
      case 'teacher':
        return '/classes';
      case 'student':
        return '/my-classes';
      case 'admin':
        return '/all-classes';
      default:
        return '/classes';
    }
  };

  const getDashboardPath = (): string => {
    if (userRole === 'owner') return '/owner-dashboard';
    if (userRole === 'admin') return '/admin-dashboard';
    return '/dashboard';
  };

  const getSchoolLogo = (schoolName?: string | { name: string }): string => {
    // Handle case where schoolName is an object with code and name properties
    if (schoolName && typeof schoolName === 'object' && schoolName.name) {
      return schoolLogos[schoolName.name] || DEFAULT_SCHOOL_LOGO;
    }
    if (typeof schoolName === 'string') {
      return schoolLogos[schoolName] || DEFAULT_SCHOOL_LOGO;
    }
    return DEFAULT_SCHOOL_LOGO;
  };
  
  const handleNavigation = (path: string, event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    navigate(path);
  };

  // Navigation items configuration
  const getNavigationItems = (): NavigationItem[] => {
    const items: NavigationItem[] = [];

    // Dashboard - always visible
    items.push({
      path: getDashboardPath(),
      icon: DashboardIcon,
      label: 'Dashboard',
      visible: true
    });

    // Owner specific items
    if (userRole === 'owner') {
      items.push(
        {
          path: '/school-management',
          icon: SchoolsIcon,
          label: 'School Management',
          visible: true
        },
        {
          path: '/user-management',
          icon: UsersIcon,
          label: 'User Management',
          visible: true
        }
      );
    }

    // Admin specific items
    if (userRole === 'admin') {
      items.push({
        path: '/users',
        icon: UsersIcon,
        label: 'Users',
        visible: true
      });
    }

    // Classes - visible for non-owner roles
    if (userRole !== 'owner') {
      items.push({
        path: getClassesPath(),
        icon: ClassesIcon,
        label: 'Classes',
        visible: true
      });
    }

    // Questions - only for teachers
    if (userRole === 'teacher') {
      items.push({
        path: '/view-questions',
        icon: QuestionsIcon,
        label: 'Questions',
        visible: true
      });
    }

    // Submissions - visible for non-owner roles
    if (userRole !== 'owner') {
      items.push({
        path: getSubmissionsPath(),
        icon: SubmissionsIcon,
        label: 'Submissions',
        visible: true
      });
    }

    // Payments - conditional visibility
    if (showPayments) {
      items.push({
        path: '/payments',
        icon: PaymentsIcon,
        label: 'Payments',
        visible: true
      });
    }

    return items.filter(item => item.visible);
  };

  const navigationItems = getNavigationItems();

  if (isMobileView) {
    // Mobile layout with perfectly evenly distributed 5 buttons
    return (
      <>
        <div style={sidebarStyles.mobile}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'end', 
            width: '100%', 
            position: 'relative',
            padding: '0',
            justifyContent: 'space-between'
          }}>
            
            {/* Button 1: Dashboard - 20% width */}
            <div style={{ 
              flex: '1', 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center'
            }}>
              <Link
                to={getDashboardPath()}
                onClick={(e) => handleNavigation(getDashboardPath(), e)}
                style={{
                  padding: '8px',
                  borderRadius: '50%',
                  transition: 'background-color 0.2s',
                  backgroundColor: isActive(getDashboardPath()) ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  if (!isActive(getDashboardPath())) {
                    e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive(getDashboardPath())) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <DashboardIcon style={{ width: '24px', height: '24px', color: '#000' }} />
              </Link>
            </div>

            {/* Button 2: Classes - 20% width */}
            <div style={{ 
              flex: '1', 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center'
            }}>
              <Link
                to={getClassesPath()}
                onClick={(e) => handleNavigation(getClassesPath(), e)}
                style={{
                  padding: '8px',
                  borderRadius: '50%',
                  transition: 'background-color 0.2s',
                  backgroundColor: isActive(getClassesPath()) ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  if (!isActive(getClassesPath())) {
                    e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive(getClassesPath())) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <ClassesIcon style={{ width: '24px', height: '24px', color: '#000' }} />
              </Link>
            </div>

            {/* Button 3: FAB (Center) - 20% width, protruding upward */}
            <div style={{ 
              flex: '1', 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'end',
              position: 'relative'
            }}>
              {(userRole === 'teacher' || userRole === 'admin') && (
                <DropdownMenu.Root>
                  <DropdownMenu.Trigger>
                    <button
                      style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #000000 0%, #333333 100%)',
                        border: '3px solid #f2d13e',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3), 0 2px 4px rgba(242, 209, 62, 0.2)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.3s ease',
                        transform: 'translateY(-12px)', // Protrude upward
                        position: 'relative',
                        zIndex: 10
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-14px)';
                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.4), 0 4px 8px rgba(242, 209, 62, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(-12px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3), 0 2px 4px rgba(242, 209, 62, 0.2)';
                      }}
                    >
                      <PlusIcon style={{ width: '24px', height: '24px', color: 'white' }} />
                    </button>
                  </DropdownMenu.Trigger>
                  
                  <DropdownMenu.Content 
                    side="top" 
                    align="center"
                    style={{
                      backgroundColor: 'white',
                      borderRadius: '12px',
                      padding: '8px',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                      border: '1px solid #e5e7eb',
                      marginBottom: '12px',
                      minWidth: '180px'
                    }}
                  >
                    <DropdownMenu.Item 
                      onClick={() => navigate('/create-class')}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '14px 16px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '15px',
                        fontWeight: '500',
                        color: '#111827',
                        backgroundColor: 'transparent',
                        border: 'none',
                        width: '100%',
                        textAlign: 'left',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <ClassesIcon style={{ width: '18px', height: '18px', color: '#f2d13e' }} />
                      New Class
                    </DropdownMenu.Item>
                    
                    {userRole === 'teacher' && (
                      <DropdownMenu.Item 
                        onClick={() => navigate('/insert-question')}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '14px 16px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '15px',
                          fontWeight: '500',
                          color: '#111827',
                          backgroundColor: 'transparent',
                          border: 'none',
                          width: '100%',
                          textAlign: 'left',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <QuestionsIcon style={{ width: '18px', height: '18px', color: '#f2d13e' }} />
                        New Question
                      </DropdownMenu.Item>
                    )}
                    
                    <DropdownMenu.Item 
                      onClick={() => navigate('/upload-student-work')}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '14px 16px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '15px',
                        fontWeight: '500',
                        color: '#111827',
                        backgroundColor: 'transparent',
                        border: 'none',
                        width: '100%',
                        textAlign: 'left',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <UploadIcon style={{ width: '18px', height: '18px', color: '#f2d13e' }} />
                      Upload Student Work
                    </DropdownMenu.Item>
                  </DropdownMenu.Content>
                </DropdownMenu.Root>
              )}
            </div>

            {/* Button 4: Submissions - 20% width */}
            <div style={{ 
              flex: '1', 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center'
            }}>
              <Link
                to={getSubmissionsPath()}
                onClick={(e) => handleNavigation(getSubmissionsPath(), e)}
                style={{
                  padding: '8px',
                  borderRadius: '50%',
                  transition: 'background-color 0.2s',
                  backgroundColor: isActive(getSubmissionsPath()) ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  if (!isActive(getSubmissionsPath())) {
                    e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive(getSubmissionsPath())) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <SubmissionsIcon style={{ width: '24px', height: '24px', color: '#000' }} />
              </Link>
            </div>

            {/* Button 5: User Profile Menu - 20% width */}
            <div style={{ 
              flex: '1', 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center'
            }}>
              <DropdownMenu.Root>
                <DropdownMenu.Trigger>
                  <button
                    style={{
                      padding: '8px',
                      borderRadius: '50%',
                      transition: 'background-color 0.2s',
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.1)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <UserIcon style={{ width: '24px', height: '24px', color: '#000' }} />
                  </button>
                </DropdownMenu.Trigger>
              
              <DropdownMenu.Content 
                side="top" 
                align="end"
                style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '8px',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                  border: '1px solid #e5e7eb',
                  marginBottom: '12px',
                  minWidth: '180px'
                }}
              >
                {/* Questions for teachers - moved to user menu */}
                {userRole === 'teacher' && (
                  <>
                    <DropdownMenu.Item 
                      onClick={() => navigate('/view-questions')}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '14px 16px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '15px',
                        fontWeight: '500',
                        color: '#111827',
                        backgroundColor: 'transparent',
                        border: 'none',
                        width: '100%',
                        textAlign: 'left',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <QuestionsIcon style={{ width: '18px', height: '18px', color: '#f2d13e' }} />
                      Questions
                    </DropdownMenu.Item>
                    <DropdownMenu.Separator style={{ height: '1px', backgroundColor: '#e5e7eb', margin: '8px 0' }} />
                  </>
                )}
                
                <DropdownMenu.Item 
                  onClick={() => setShowHelpPopup(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '14px 16px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '15px',
                    fontWeight: '500',
                    color: '#111827',
                    backgroundColor: 'transparent',
                    border: 'none',
                    width: '100%',
                    textAlign: 'left',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <HelpIcon style={{ width: '18px', height: '18px', color: '#6b7280' }} />
                  Help
                </DropdownMenu.Item>
                
                {showPayments && (
                  <DropdownMenu.Item 
                    onClick={() => navigate('/payments')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '14px 16px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '15px',
                      fontWeight: '500',
                      color: '#111827',
                      backgroundColor: 'transparent',
                      border: 'none',
                      width: '100%',
                      textAlign: 'left',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <PaymentsIcon style={{ width: '18px', height: '18px', color: '#6b7280' }} />
                    Payments
                  </DropdownMenu.Item>
                )}
                
                <DropdownMenu.Item 
                  onClick={() => setShowSettingsPopup(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '14px 16px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '15px',
                    fontWeight: '500',
                    color: '#111827',
                    backgroundColor: 'transparent',
                    border: 'none',
                    width: '100%',
                    textAlign: 'left',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <SettingsIcon style={{ width: '18px', height: '18px', color: '#6b7280' }} />
                  Settings
                </DropdownMenu.Item>
                
                <DropdownMenu.Separator style={{ height: '1px', backgroundColor: '#e5e7eb', margin: '8px 0' }} />
                
                <DropdownMenu.Item 
                  onClick={handleLogout}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '14px 16px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '15px',
                    fontWeight: '500',
                    color: '#ef4444',
                    backgroundColor: 'transparent',
                    border: 'none',
                    width: '100%',
                    textAlign: 'left',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <LogoutIcon style={{ width: '18px', height: '18px' }} />
                  Logout
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Root>
            </div>
          </div>
        </div>
        
        {showHelpPopup && (
          <HelpPopup 
            isOpen={showHelpPopup}
            onClose={() => setShowHelpPopup(false)}
            userRole={userRole}
          />
        )}

        <SettingsPopup
          show={showSettingsPopup}
          onHide={() => setShowSettingsPopup(false)}
          userName={displayName}
          userRole={userRole}
          schoolName={user?.school && typeof user.school === 'object' ? user.school.name : (typeof user?.school === 'string' ? user.school : '')}
          schoolLogo={user?.school ? getSchoolLogo(user.school) : ''}
        />
      </>
    );
  }

  // Desktop layout
  return (
    <>
      <div style={sidebarStyles.desktop}>
        {/* Header with Logo */}
        <div style={sidebarStyles.header}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '100%', maxWidth: '200px' }}>
              <img 
                src={snapgradeLogo} 
                alt="SnapGrade Logo" 
                style={sidebarStyles.logo}
              />
            </div>
{/* School logo only shown for Classroom Success Plan users */}
            {user?.school && user?.plan === 'classroom' && (
              <img 
                src={getSchoolLogo(user.school)} 
                alt={`${typeof user.school === 'object' ? user.school.name : user.school} Logo`}
                style={sidebarStyles.schoolLogo}
              />
            )}
          </div>
        </div>

        {/* Navigation */}
        <div style={sidebarStyles.nav}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {navigationItems.map((item) => {
              const IconComponent = item.icon;
              const active = isActive(item.path);
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={(e) => handleNavigation(item.path, e)}
                  style={{
                    ...sidebarStyles.navItem,
                    ...(active ? sidebarStyles.navItemActive : {})
                  }}
                  onMouseEnter={(e) => {
                    if (!active) {
                      e.currentTarget.style.backgroundColor = sidebarStyles.navItemHover.backgroundColor;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!active) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <IconComponent style={{ width: '20px', height: '20px', flexShrink: 0 }} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>

          <div style={sidebarStyles.supportSection}>
            <div style={sidebarStyles.supportTitle}>
              SUPPORT
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <button
                onClick={() => setShowHelpPopup(true)}
                style={{
                  ...sidebarStyles.navItem,
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  width: '100%',
                  textAlign: 'left'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = sidebarStyles.navItemHover.backgroundColor}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <HelpIcon style={{ width: '20px', height: '20px', flexShrink: 0 }} />
                <span>Help</span>
              </button>
              <button
                onClick={() => setShowSettingsPopup(true)}
                style={{
                  ...sidebarStyles.navItem,
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  width: '100%',
                  textAlign: 'left'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = sidebarStyles.navItemHover.backgroundColor}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <SettingsIcon style={{ width: '20px', height: '20px', flexShrink: 0 }} />
                <span>Settings</span>
              </button>
            </div>
          </div>
        </div>

        {/* User Profile */}
        <div style={sidebarStyles.userProfile}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={sidebarStyles.avatar}>
                {displayName.charAt(0).toUpperCase() || 'A'}
              </div>
              <div>
                <div style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                  Welcome {displayName}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px' }}>
                  <div 
                    style={{
                      ...sidebarStyles.roleBadge,
                      backgroundColor: getRoleBadgeColor(userRole)
                    }}
                  />
                  <span style={{ fontSize: '12px', color: '#6b7280', textTransform: 'capitalize' }}>
                    {userRole}
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={handleLogout}
              style={{
                padding: '6px',
                borderRadius: '6px',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              title="Log Out"
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <LogoutIcon style={{ width: '16px', height: '16px', color: '#ef4444' }} />
            </button>
          </div>
        </div>
      </div>

      {showHelpPopup && (
        <HelpPopup 
          isOpen={showHelpPopup}
          onClose={() => setShowHelpPopup(false)}
          userRole={userRole}
        />
      )}

      <SettingsPopup
        show={showSettingsPopup}
        onHide={() => setShowSettingsPopup(false)}
        userName={displayName}
        userRole={userRole}
        schoolName={user?.school && typeof user.school === 'object' ? user.school.name : (typeof user?.school === 'string' ? user.school : '')}
        schoolLogo={user?.school ? getSchoolLogo(user.school) : ''}
      />
    </>
  );
};

export default Sidebar; 